import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, CircularProgress, DialogActions, DialogContent, Typography } from '@mui/material';
import { getApplicationSettings } from 'src/modules/applications/slices';
import { BhiDialog } from 'src/UIKit';
import {
  PreviewSplitAside,
  PreviewSplitHeader,
  PreviewSplitLayout,
  PreviewSplitMain,
} from './PreviewSplitLayout.styles';
import BannedWordsModal from 'src/components/BannedWordsModal/view';
import {
  BuilderProvider,
  CampaignFormProvider,
  EmbeddedBuilderProvider,
  MessageValidityProvider,
  useMessageValidity,
} from '../../context';
import {
  CampaignBuilderParams,
  DynamicCampaignBuilderParams,
  useCampaignBuilderController,
  useDynamicCampaignBuilderViewController,
} from '../../controllers';
import { PolicyFields } from './PolicyFields';
import { PrimaryActions, useRunSave } from './Actions';
import { HeaderActions } from './HeaderActions';
import { Phone } from './Phone';
import { ActivationGateDialog } from './ActivationGateDialog';
import { Campaign } from '../../types';
import { toContactToGroupConfig } from '../../helpers';

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
    {children}
  </Box>
);

export const DynamicCampaignBuilder: React.FC<DynamicCampaignBuilderParams> = (params) => {
  const { isPageLoading, error, builder } = useDynamicCampaignBuilderViewController(params);

  // In-flow spinner and message. A fixed/inset-0 overlay would ignore the sidebar
  // and centre itself over the whole viewport.
  if (isPageLoading) return <Centered><CircularProgress /></Centered>;
  if (error) return <Centered><Typography color="error">{error}</Typography></Centered>;
  // The loader always reports an error when it resolves no config, so this is a
  // guard rather than a reachable state.
  if (!builder) return null;
  return <Builder {...builder} />;
};

type EmbeddedDialogState = { dirty: boolean; busy: boolean };

type EmbeddedDialogControls = {
  onCancel: () => void;
  /** Lets the dialog shell run its own close logic against the loaded builder's state. */
  onStateChange: (state: EmbeddedDialogState) => void;
};

/**
 * Same fields, validation and activation flow as the full-page builder. One dialog stays
 * mounted from loading to editing, so the modal does not close and reopen when the config arrives.
 */
export const CampaignBuilderDialog = ({ onSaved, onClose, initialName }: {
  onSaved: (campaign: Campaign) => void;
  onClose: () => void;
  /** Replaces the config's dated default, so the host can name the campaign after itself. */
  initialName?: string;
}) => {
  // Same application scope as campaign type selection. Any join config of it will do: the dialog
  // empties the messages, drops keywords and fixes the saved type.
  const applicationId = useSelector(getApplicationSettings)?.applicationId;
  const { isPageLoading, error, builder } = useDynamicCampaignBuilderViewController({
    configIdOrType: 'join', applicationId, onSaved,
  });
  const contactToGroupBuilder = useMemo(
    () => builder && {
      ...builder,
      config: toContactToGroupConfig(builder.config),
      initialName: initialName ?? builder.initialName,
    },
    [builder, initialName],
  );
  const [{ dirty, busy }, setBuilderState] = useState<EmbeddedDialogState>({ dirty: false, busy: false });
  const [discardOpen, setDiscardOpen] = useState(false);
  const ready = !isPageLoading && !error && contactToGroupBuilder;
  const requestClose = () => {
    if (busy) return;
    if (dirty) setDiscardOpen(true);
    else onClose();
  };

  return (
    <>
      <BhiDialog
        open
        title="Create join campaign"
        onClose={requestClose}
        showCloseButton={!busy}
        fullWidth
        maxWidth="lg"
      >
        {ready ? (
          <Builder
            {...contactToGroupBuilder}
            startWithEmptyMessages
            // An application's config would otherwise save as `custom`; contact capture needs Contact-To-Group.
            externalType="contact_to_group"
            embeddedDialog={{ onCancel: requestClose, onStateChange: setBuilderState }}
          />
        ) : (
          <>
            <DialogContent>
              <Centered>{error ? <Typography color="error">{error}</Typography> : <CircularProgress />}</Centered>
            </DialogContent>
            <DialogFooter busy={false} saveDisabled onCancel={onClose} />
          </>
        )}
      </BhiDialog>
      <BhiDialog open={discardOpen} title="Discard campaign changes?" onClose={() => setDiscardOpen(false)}>
        <DialogContent>Your contact capture changes will be kept.</DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDiscardOpen(false)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={onClose}>Discard campaign</Button>
        </DialogActions>
      </BhiDialog>
    </>
  );
};

const Builder: React.FC<CampaignBuilderParams & { embeddedDialog?: EmbeddedDialogControls }> = (params) => {
  const {
    policy, form, builderContext, title, isCampaignReadOnly,
    save, blocklist, activationGate, KeywordConfirmationDialog, navigationGuard,
    actions, headerActions, isSaving,
  } = useCampaignBuilderController(params);

  // The dialog footer's save; the full-page row tracks its own through PrimaryActions.
  const { busy: dialogSaving, run } = useRunSave();
  const { embeddedDialog } = params;
  const embedded = Boolean(embeddedDialog);
  // isSaving also covers saves started from the builder's own modals (blocklist, activation gate).
  const busy = dialogSaving || isSaving;
  const reportState = embeddedDialog?.onStateChange;
  useEffect(() => {
    reportState?.({ dirty: form.isDirty, busy });
  }, [reportState, form.isDirty, busy]);

  const editor = (
    <>
      {!embedded && (
        <PreviewSplitHeader>
          <Box>
            <Typography variant="h6" id="customized-dialog-title" sx={{ p: 0 }}>{title}</Typography>
            {builderContext.config.description && (
              <Typography variant="body2" color="text.secondary">{builderContext.config.description}</Typography>
            )}
          </Box>
          <HeaderActions {...headerActions} />
        </PreviewSplitHeader>
      )}
      <PreviewSplitLayout>
        <PreviewSplitMain embedded={embedded}>
          <PolicyFields policy={policy} config={builderContext.config} />
          {!embedded && !isCampaignReadOnly && <PrimaryActions onSubmit={save} {...actions} saving={isSaving} />}
        </PreviewSplitMain>
        {!embedded && <PreviewSplitAside><Phone /></PreviewSplitAside>}
      </PreviewSplitLayout>
    </>
  );

  const dialogs = (
    <>
      <BannedWordsModal
        isOpen={blocklist.modalOpen}
        onCancel={() => blocklist.closeModal()}
        onConfirm={() => blocklist.confirm()}
      />
      {/* Leaving for account setup would abandon the contact capture around the dialog. */}
      <ActivationGateDialog {...activationGate} onCompleteSetup={embedded ? undefined : activationGate.onCompleteSetup} />
      <KeywordConfirmationDialog />
      <BhiDialog open={navigationGuard.open} title="You have unsaved changes!">
        <DialogContent>Do you want to continue without saving?</DialogContent>
        <DialogActions>
          <Button color="primary" variant="outlined" onClick={navigationGuard.cancel}>No</Button>
          <Button color="secondary" variant="contained" onClick={navigationGuard.confirm}>Yes</Button>
        </DialogActions>
      </BhiDialog>
    </>
  );

  return (
    <BuilderProvider value={builderContext}>
      <CampaignFormProvider value={form}>
        <EmbeddedBuilderProvider value={embedded}>
          {/* Editors and both action layouts share the same send-limit verdicts. */}
          <MessageValidityProvider>
            {embeddedDialog ? (
              <>
                <DialogContent>{editor}</DialogContent>
                <EmbeddedDialogFooter
                  busy={busy}
                  disabled={Boolean(actions.primaryDisabled) || isCampaignReadOnly}
                  onCancel={embeddedDialog.onCancel}
                  onSave={() => run(() => save('active', 'create', true))}
                />
              </>
            ) : editor}
            {dialogs}
          </MessageValidityProvider>
        </EmbeddedBuilderProvider>
      </CampaignFormProvider>
    </BuilderProvider>
  );
};

const EmbeddedDialogFooter = ({ disabled, ...props }: Omit<React.ComponentProps<typeof DialogFooter>, 'saveDisabled'> & {
  disabled: boolean;
}) => {
  const { hasInvalid } = useMessageValidity();
  return <DialogFooter {...props} saveDisabled={disabled || hasInvalid} />;
};

const DialogFooter = ({ busy, saveDisabled, onCancel, onSave }: {
  busy: boolean;
  saveDisabled: boolean;
  onCancel: () => void;
  onSave?: () => unknown;
}) => (
  <DialogActions>
    <Button variant="outlined" onClick={onCancel} disabled={busy}>Cancel</Button>
    <Button variant="contained" onClick={onSave} disabled={busy || saveDisabled}>Save</Button>
  </DialogActions>
);
